import type { Balance, ChainType } from '@polaris/core/models'
import {
  formatCoin,
  formatPrice,
  getAddressFromEcosystemAddresses,
  shorten,
} from '@polaris/core/utils'
import { useAtomValue, useSetAtom } from 'jotai'
import { Check, CircleAlert, Info } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useDeleteBalancesCache } from '@/src/hooks/balances/use-delete-balances-cache'
import { useMpcBalances } from '@/src/hooks/mpc/use-mpc-balances'
import { useMpcRecoverFunds } from '@/src/hooks/mpc/use-mpc-recover-funds'
// useDeleteBalancesCache hook will be used instead of direct service call
import { mpcWalletAddressesWithNamadaOverrideAtom } from '@/src/stores/mpc/atoms'
import { isRunningAtom } from '@/src/stores/route-machine/atoms'
import {
  openSelectWalletsDialog,
  selectWalletsDialogAtom,
} from '@/src/stores/select-wallets'
import {
  walletKeyringAtom,
  walletOptionsAtom,
} from '@/src/stores/wallets/atoms'
import { cn } from '@/src/utils/styles'
import { Button } from '../ui/button'
import CopyButton from '../ui/copy-button'
import {
  ListRow,
  ListRowImage,
  ListRowSide,
  ListRowSideDescription,
  ListRowSideName,
} from '../ui/list-row'
import { Spinner } from '../ui/spinner'

export const MpcDetails = () => {
  const { totalUsdValue, balancesByChain } = useMpcBalances()
  const isTradeRunning = useAtomValue(isRunningAtom)

  const invalidatedOnOpenRef = useRef(false)
  const { mutate: deleteBalances } = useDeleteBalancesCache()

  useEffect(() => {
    if (invalidatedOnOpenRef.current) return

    if (balancesByChain.length === 0) return

    invalidatedOnOpenRef.current = true
    deleteBalances()
  }, [deleteBalances, balancesByChain])

  if (balancesByChain.length === 0) return null

  return (
    <div className='animate-fade-in'>
      <div className='py-3'>
        <div className='text-secondary'>Total balance</div>
        <div className='text-4xl font-semibold text-primary'>
          {formatPrice(totalUsdValue)}
        </div>
      </div>

      <section className='py-6'>
        <h2 className='text-xl font-semibold text-primary pb-3'>Balances</h2>
        <p className='text-secondary text-sm pb-3 leading-6'>
          If your trade has failed, you may recover any funds by sending to a
          compatible connected wallet.
        </p>

        {isTradeRunning && (
          <div className='flex items-start gap-3 p-4 mb-4 bg-surface1 rounded-xl border border-border2'>
            <Info className='text-secondary flex-shrink-0 mt-0.5' size={20} />
            <div className='flex flex-col gap-1'>
              <p className='text-sm font-semibold text-primary'>
                Recovery unavailable
              </p>
              <p className='text-sm text-secondary leading-5'>
                Recovery is unavailable while any trade is still running. Please
                wait for your trade to complete before recovering funds.
              </p>
            </div>
          </div>
        )}

        <div className='py-3'>
          {balancesByChain.map(
            ({ chain_id, addresses, chain_name, balances }) => (
              <div key={chain_id}>
                <div className='flex items-center place-content-between'>
                  <h2 className='text-secondary text-sm'>
                    {chain_name} {balances.length > 1 ? 'balances' : 'balance'}
                  </h2>
                  <CopyButton
                    contentToCopy={addresses[0]}
                    displayContent={shorten(addresses[0])}
                  />
                </div>
                <div className='space-y-2 py-3'>
                  {balances.map((balance) => {
                    const key = balance.chain_id + balance.denom
                    return (
                      <BalanceTransferRow
                        key={key}
                        {...balance}
                        isTradeRunning={isTradeRunning}
                      />
                    )
                  })}
                </div>
              </div>
            ),
          )}
        </div>
      </section>
    </div>
  )
}

const BalanceTransferRow = ({
  normalized_amount,
  chain_id,
  chain_type,
  chain_name,
  ticker,
  denom,
  logo_img_url,
  value_usd,
  chain_img,
  display_name,
  isTradeRunning,
}: Balance & { isTradeRunning: boolean }) => {
  const wallets = useAtomValue(walletOptionsAtom)
  const walletKeyring = useAtomValue(walletKeyringAtom)
  const connectedWallet = wallets.connectedWallets[chain_type as ChainType]
  const setDialogState = useSetAtom(selectWalletsDialogAtom)

  const { data: mpcAddresses } = useAtomValue(
    mpcWalletAddressesWithNamadaOverrideAtom,
  )
  const { mutate, isSuccess, isError, isPending, error } = useMpcRecoverFunds()

  const ctaDisabled = isSuccess || isPending || isTradeRunning

  return (
    <div className='flex flex-col bg-surface1 rounded-xl'>
      <ListRow>
        {isSuccess ? (
          <div className='flex items-center text-success-text'>
            <div className='flex items-center justify-center w-10 h-10 bg-surface1 rounded-full mr-2'>
              <Check height={24} width={24} />
            </div>
            Sent to {connectedWallet?.displayName ?? 'Wallet'}
          </div>
        ) : isError ? (
          <div className='flex items-center text-danger-text'>
            <div className='flex items-center justify-center w-10 h-10 bg-surface1 rounded-full mr-2'>
              <CircleAlert height={24} width={24} />
            </div>
            <div className='flex items-center gap-1'>
              <span className='flex-1'>
                {error?.message ||
                  'Failed to send to ' +
                    (connectedWallet?.displayName ?? 'Wallet')}
              </span>
              {error?.details?.address && (
                <CopyButton
                  contentToCopy={error.details.address}
                  displayContent=''
                  size='md'
                  variant='ghost'
                  className='h-6 w-6 p-0'
                  title={`Copy address: ${error.details.address}`}
                />
              )}
            </div>
          </div>
        ) : (
          <>
            {logo_img_url && (
              <ListRowImage
                src={logo_img_url}
                alt={`${ticker} icon`}
                width={48}
                height={48}
                className='rounded-full h-12 w-12'
                classes={{ container: 'flex-shrink-0' }}
                unoptimized
                chainImageAlt={chain_name}
                chainImageSrc={chain_img}
                chainId={chain_id}
                placeholderName={display_name}
              />
            )}
            <ListRowSide containerClassName='w-full'>
              <ListRowSideName>{ticker}</ListRowSideName>
              <ListRowSideDescription>on {chain_name}</ListRowSideDescription>
            </ListRowSide>

            <ListRowSide className='text-right'>
              <ListRowSideName>{formatPrice(value_usd)}</ListRowSideName>
              <ListRowSideDescription>
                {formatCoin({
                  amount: normalized_amount,
                })}
              </ListRowSideDescription>
            </ListRowSide>
          </>
        )}
      </ListRow>
      <hr className='border-surface1' />
      <div className='px-8 py-4'>
        <Button
          onClick={() => {
            const toAddress = walletKeyring.getAddress(chain_type, chain_id)
            let fromAddress: string | undefined

            if (mpcAddresses) {
              fromAddress = getAddressFromEcosystemAddresses(
                mpcAddresses,
                chain_type,
              )
            }

            if (!connectedWallet || !toAddress) {
              setDialogState(openSelectWalletsDialog(chain_type))
              return
            }

            mutate({
              chainID: chain_id,
              amount: normalized_amount,
              assetDenom: denom,
              fromAddress,
              toAddress,
            })
          }}
          className={cn(
            'w-full !rounded-t-none rounded-b-xl hover:rounded-b-xl',
            {
              'opacity-50': ctaDisabled,
            },
          )}
          disabled={ctaDisabled}
        >
          {isPending ? (
            <>
              <Spinner /> Sending
            </>
          ) : isTradeRunning ? (
            'Recovery unavailable - trade in progress'
          ) : !normalized_amount ? (
            'Balance too low to send'
          ) : connectedWallet ? (
            <>
              Send to {connectedWallet.displayName}
              {connectedWallet.logoUrl && (
                <ListRowImage
                  width={16}
                  height={16}
                  alt='wallet logo'
                  src={connectedWallet.logoUrl}
                  classes={{ container: 'flex-shrink-0' }}
                  unoptimized
                  placeholderName={connectedWallet?.displayName ?? ''}
                />
              )}
            </>
          ) : (
            <>Connect {chain_name} wallet</>
          )}
        </Button>
      </div>
    </div>
  )
}
